<template>
  <div v-if="visible" class="user-support-widget" :class="{ expanded }">
    <section v-if="expanded && !isSmallScreen" class="support-chat-popover">
      <header class="support-chat-head">
        <div>
          <p>在线客服</p>
          <strong>龙珠电竞客服</strong>
        </div>
        <div class="head-actions">
          <button type="button" @click="goSupportPage">完整页面</button>
          <button type="button" @click="expanded = false">收起</button>
        </div>
      </header>

      <div v-if="supportStatusText" class="support-status" :class="supportStatusClass">
        {{ supportStatusText }}
      </div>

      <div class="support-tools">
        <div class="faq-tabs">
          <button type="button" class="faq-main" @click="toggleFaqPanel">
            {{ faqPanelOpen ? '收起问题' : '常见问题' }}
          </button>
          <button
            v-for="group in faqGroups"
            :key="group.key"
            type="button"
            class="faq-chip"
            :class="{ active: faqPanelOpen && activeFaqGroup === group.key }"
            @click="openFaqGroup(group.key)"
          >
            {{ group.label }}
          </button>
        </div>
        <a-button
          v-if="conversation?.status === 'closed'"
          size="small"
          type="primary"
          class="human-btn"
          :loading="loading"
          @click="startNewConversation"
        >
          重新咨询
        </a-button>
        <a-button
          v-else
          size="small"
          type="primary"
          class="human-btn"
          :loading="handoffLoading"
          :disabled="humanActionDisabled"
          @click="requestHuman"
        >
          {{ humanActionText }}
        </a-button>
      </div>

      <main ref="messagePaneRef" class="support-chat-body">
        <div v-if="loading" class="support-empty">正在连接客服...</div>

        <section v-if="faqPanelOpen" class="faq-panel">
          <div class="faq-panel-title">
            <span>智能客服</span>
            <strong>先点一个问题看看答案</strong>
          </div>
          <article v-for="item in displayedFaqReplies" :key="item.id" class="faq-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.content }}</p>
            <div v-if="item.images?.length" class="reply-images">
              <img v-for="image in item.images" :key="image" :src="buildImageUrl(image)" alt="回复配图">
            </div>
            <button type="button" @click="useFaqQuestion(item)">继续咨询这个问题</button>
          </article>
          <div v-if="!displayedFaqReplies.length" class="support-empty compact">
            暂无常见问题，您可以直接输入。
          </div>
        </section>

        <section v-if="autoReplies.length" class="auto-reply-panel">
          <div class="faq-panel-title">
            <span>智能回复</span>
            <strong>这些答案可能有用</strong>
          </div>
          <article v-for="reply in autoReplies" :key="reply.id" class="faq-card">
            <h3>{{ reply.title }}</h3>
            <p>{{ reply.content }}</p>
            <div v-if="reply.images?.length" class="reply-images">
              <img v-for="image in reply.images" :key="image" :src="buildImageUrl(image)" alt="回复配图">
            </div>
          </article>
        </section>
        <div v-else-if="searched && lastAutoReplyQuery" class="auto-reply-empty">
          没有找到匹配答案，可以继续发送问题或呼叫人工客服。
        </div>

        <div
          v-for="item in messages"
          :key="item.id"
          class="message-row"
          :class="{
            mine: item.sender_role === 'user',
            system: item.sender_role === 'system',
            'account-link-row': item.message_type === 'account_link'
          }"
        >
          <div class="message-bubble" :class="{ 'account-link-bubble': item.message_type === 'account_link' }">
            <div class="message-meta">
              <MemberName :name="getSenderLabel(item)" :is-member="item.sender_is_member" />
              <time>{{ formatTime(item.created_at) }}</time>
            </div>
            <button
              v-if="item.message_type === 'account_link'"
              type="button"
              class="support-account-card"
              @click="openAccountLink(item)"
            >
              <div class="support-account-card-head">
                <span>账号 #{{ item.payload?.account_number || item.payload?.account_id }}</span>
                <em :class="{ pending: !item.payload?.order_id }">{{ getAccountLinkOrderText(item) }}</em>
              </div>
              <h3>{{ getAccountLinkTitle(item) }}</h3>
              <div class="support-account-card-summary">
                <span>
                  <small>哈夫币</small>
                  <strong>{{ formatHaf(item.payload?.haf_coins) }}</strong>
                </span>
                <span>
                  <small>预付款</small>
                  <strong>{{ formatPayloadMoney(item.payload?.prepay_amount || item.payload?.order_total_amount) }}</strong>
                </span>
              </div>
              <div class="support-account-card-stats">
                <span v-for="meta in getAccountLinkMetaItems(item)" :key="meta.label">
                  <small>{{ meta.label }}</small>
                  <strong>{{ meta.value }}</strong>
                </span>
              </div>
              <div class="support-account-card-foot">
                <span>查看账号详情与付款状态</span>
              </div>
            </button>
            <p v-else>{{ item.content }}</p>
          </div>
        </div>
      </main>

      <footer class="support-composer">
        <a-textarea
          v-model:value="draft"
          :disabled="!canSendMessage"
          :auto-size="{ minRows: 1, maxRows: 3 }"
          :placeholder="canSendMessage ? '输入问题，Enter 发送...' : '人工服务已结束'"
          @pressEnter="handleEnter"
        />
        <a-button type="primary" :disabled="!canSendMessage" :loading="sending" @click="send">
          发送
        </a-button>
      </footer>
    </section>

    <button
      type="button"
      class="support-float-button"
      :class="{ unread: unreadCount > 0 }"
      aria-label="在线客服"
      title="在线客服"
      @click="toggleExpanded"
    >
      <CustomerServiceOutlined />
      <span class="support-float-label">客服</span>
      <em v-if="unreadCount">{{ unreadCount > 99 ? '99+' : unreadCount }}</em>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { CustomerServiceOutlined } from '@ant-design/icons-vue'
import { supportApi } from '@/https/api'
import { useAuthStore } from '@/stores'
import { useResponsive } from '@/composables'
import type { SupportAutoReply, SupportConversation, SupportMessage } from '@/https/types/support'
import MemberName from '@/components/common/MemberName.vue'
import { buildImageUrl } from '@/utils/imageHelpers'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { isMobile } = useResponsive()

const conversation = ref<SupportConversation | null>(null)
const messages = ref<SupportMessage[]>([])
const draft = ref('')
const autoReplies = ref<SupportAutoReply[]>([])
const faqReplies = ref<SupportAutoReply[]>([])
const activeFaqGroup = ref('租客')
const faqPanelOpen = ref(false)
const expanded = ref(false)
const loading = ref(false)
const sending = ref(false)
const handoffLoading = ref(false)
const searched = ref(false)
const lastAutoReplyQuery = ref('')
const messagePaneRef = ref<HTMLElement | null>(null)
let pollTimer: number | undefined
let pollInFlight = false

const faqGroups = [
  { key: '租客', label: '租客' },
  { key: '号主', label: '号主' },
  { key: '违规', label: '违规' }
]

const isSmallScreen = computed(() => isMobile.value)
const visible = computed(() => {
  const path = route.path
  return authStore.isAuthenticated && !authStore.canAccessAdmin && !path.startsWith('/admin') && path !== '/support'
})

const unreadCount = computed(() => conversation.value?.user_unread_count || 0)

const supportStatusText = computed(() => {
  if (!conversation.value) return ''
  if (conversation.value.status === 'closed') return '本次人工服务已结束。'
  if (conversation.value.assigned_admin_name) return `客服 ${conversation.value.assigned_admin_name} 已接入，请留意回复。`
  if (conversation.value.handoff_requested) return '已呼叫人工客服，正在排队等待接入。'
  return '智能客服可先解答常见问题，需要人工时可随时呼叫。'
})

const supportStatusClass = computed(() => {
  if (!conversation.value) return ''
  if (conversation.value.status === 'closed') return 'closed'
  if (conversation.value.assigned_admin_name) return 'assigned'
  if (conversation.value.handoff_requested) return 'waiting'
  return 'smart'
})

const humanActionText = computed(() => {
  if (!conversation.value) return '呼叫人工'
  if (conversation.value.status === 'closed') return '已结束'
  if (conversation.value.assigned_admin_name) return '已接入'
  if (conversation.value.handoff_requested) return '排队中'
  return '呼叫人工'
})

const humanActionDisabled = computed(() => (
  handoffLoading.value ||
  conversation.value?.status === 'closed' ||
  !!conversation.value?.assigned_admin_name ||
  !!conversation.value?.handoff_requested
))

const canSendMessage = computed(() => !!conversation.value && conversation.value.status === 'open')

const displayedFaqReplies = computed(() => {
  const filtered = faqReplies.value
    .filter((item) => {
      const text = `${item.title} ${item.content} ${(item.keywords || []).join(' ')}`
      return text.includes(activeFaqGroup.value)
    })
    .slice(0, 4)
  return filtered.length ? filtered : faqReplies.value.slice(0, 4)
})

const payloadText = (payload: Record<string, unknown> | undefined, key: string) => {
  const value = payload?.[key]
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

const payloadNumber = (payload: Record<string, unknown> | undefined, key: string) => {
  const value = payload?.[key]
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const formatTime = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
}

const getSenderLabel = (item: SupportMessage) => {
  if (item.sender_role === 'user') return '我'
  if (item.sender_role === 'system') return '智能客服'
  return item.sender_name || '客服'
}

const getAccountLinkOrderText = (item: SupportMessage) => {
  const status = String(item.payload?.order_status || '')
  if (status === 'PENDING') return '待付款'
  return String(item.payload?.order_status_display || '待客户下单')
}

const formatPayloadMoney = (value: unknown) => {
  const number = Number(value || 0)
  if (!Number.isFinite(number) || number <= 0) return '待确认'
  return `¥${(number / 100).toFixed(2)}`
}

const formatHaf = (value: unknown) => {
  const number = Number(value || 0)
  if (!Number.isFinite(number) || number <= 0) return '待确认'
  return `${number.toFixed(number % 1 === 0 ? 0 : 2)}M`
}

const getAccountLinkTitle = (item: SupportMessage) => {
  return payloadText(item.payload, 'account_title') || `三角洲行动账号 #${item.payload?.account_number || item.payload?.account_id || ''}`
}

const getAccountLinkMetaItems = (item: SupportMessage) => {
  const payload = item.payload
  const loginMethod = payloadText(payload, 'login_method_display') || payloadText(payload, 'login_method')
  const province = payloadText(payload, 'common_login_province')
  const maxDays = payloadNumber(payload, 'max_rentable_days')
  const daily = payloadNumber(payload, 'daily_consumption_m')
  const equipment = [
    payloadNumber(payload, 'insurance_slots') ? `${payloadNumber(payload, 'insurance_slots')}格` : '',
    payloadNumber(payload, 'stamina') ? `${payloadNumber(payload, 'stamina')}体` : '',
    payloadNumber(payload, 'carrying_capacity') ? `${payloadNumber(payload, 'carrying_capacity')}负` : ''
  ].filter(Boolean).join(' / ') || '待确认'
  return [
    { label: '计费', value: payloadText(payload, 'billing_mode_display') || '待确认' },
    { label: '登录', value: [loginMethod, province].filter(Boolean).join(' / ') || '待确认' },
    { label: '最多', value: maxDays && maxDays > 0 ? `${maxDays}天` : '不足1天' },
    { label: '速度', value: daily && daily > 0 ? `${daily.toFixed(daily % 1 === 0 ? 0 : 1)}M/天` : '待确认' },
    { label: '配置', value: equipment }
  ]
}

const scrollToBottom = async () => {
  await nextTick()
  const pane = messagePaneRef.value
  if (pane) pane.scrollTop = pane.scrollHeight
}

const loadMessages = async (keepPosition = false) => {
  if (!conversation.value) return
  const list = await supportApi.listMessages(conversation.value.id)
  messages.value = list
  if (!keepPosition) await scrollToBottom()
}

const refreshConversationState = async () => {
  if (conversation.value?.id) {
    conversation.value = await supportApi.getConversation(conversation.value.id)
  } else {
    conversation.value = await supportApi.getActiveConversation()
  }
}

const loadFaqReplies = async () => {
  try {
    const result = await supportApi.listAutoReplies({ page: 1, size: 8 })
    faqReplies.value = result.items || []
  } catch (error) {
    faqReplies.value = []
  }
}

const initConversation = async () => {
  if (!visible.value || loading.value) return
  loading.value = true
  try {
    conversation.value = await supportApi.getActiveConversation()
    await Promise.all([loadMessages(), loadFaqReplies()])
  } catch (error) {
    // Keep the launcher visible; opening /support is still available.
  } finally {
    loading.value = false
  }
}

const markRead = async () => {
  if (!conversation.value || unreadCount.value <= 0) return
  try {
    await supportApi.markRead(conversation.value.id)
    conversation.value.user_unread_count = 0
  } catch (error) {
    // Non-critical.
  }
}

const stopPolling = () => {
  if (pollTimer) window.clearTimeout(pollTimer)
  pollTimer = undefined
}

const poll = async () => {
  if (pollInFlight || document.hidden || !visible.value || !conversation.value) return
  pollInFlight = true
  try {
    await refreshConversationState()
    await loadMessages(true)
  } finally {
    pollInFlight = false
  }
}

const schedulePolling = () => {
  stopPolling()
  if (document.hidden || !visible.value) return
  pollTimer = window.setTimeout(async () => {
    pollTimer = undefined
    await poll()
    schedulePolling()
  }, 8000)
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopPolling()
    return
  }
  void poll()
  schedulePolling()
}

const startPolling = () => {
  stopPolling()
  schedulePolling()
}

const toggleExpanded = async () => {
  if (isSmallScreen.value) {
    goSupportPage()
    return
  }
  expanded.value = !expanded.value
  if (expanded.value) {
    await initConversation()
    await markRead()
    await scrollToBottom()
  }
}

const goSupportPage = () => {
  expanded.value = false
  router.push('/support')
}

const openFaqGroup = async (groupKey: string) => {
  activeFaqGroup.value = groupKey
  faqPanelOpen.value = true
  autoReplies.value = []
  searched.value = false
  lastAutoReplyQuery.value = ''
  await initConversation()
}

const toggleFaqPanel = async () => {
  faqPanelOpen.value = !faqPanelOpen.value
  if (faqPanelOpen.value) {
    autoReplies.value = []
    searched.value = false
    lastAutoReplyQuery.value = ''
    await initConversation()
  }
}

const useFaqQuestion = (item: SupportAutoReply) => {
  draft.value = item.title
  faqPanelOpen.value = false
}

const matchAutoReplies = async (rawQuery?: string) => {
  const query = (rawQuery ?? draft.value).trim()
  if (!query) {
    autoReplies.value = []
    searched.value = false
    lastAutoReplyQuery.value = ''
    return
  }
  try {
    autoReplies.value = await supportApi.matchAutoReplies(query)
    searched.value = true
    lastAutoReplyQuery.value = query
  } catch (error) {
    autoReplies.value = []
    searched.value = true
    lastAutoReplyQuery.value = query
  }
}

const send = async () => {
  const content = draft.value.trim()
  if (!content || !conversation.value) return
  if (!canSendMessage.value) {
    message.warning('人工服务已结束')
    return
  }
  sending.value = true
  try {
    const shouldSearch = !autoReplies.value.length && !searched.value
    await supportApi.sendMessage(conversation.value.id, { content })
    draft.value = ''
    faqPanelOpen.value = false
    if (shouldSearch) await matchAutoReplies(content)
    else {
      autoReplies.value = []
      searched.value = false
      lastAutoReplyQuery.value = ''
    }
    await loadMessages()
  } catch (error) {
    message.error('发送失败，请稍后再试')
  } finally {
    sending.value = false
  }
}

const requestHuman = async () => {
  if (!conversation.value || conversation.value.status === 'closed') return
  handoffLoading.value = true
  try {
    conversation.value = await supportApi.requestHuman(conversation.value.id)
    autoReplies.value = []
    searched.value = false
    lastAutoReplyQuery.value = ''
    faqPanelOpen.value = false
    message.success('已呼叫人工客服，请稍候')
    await loadMessages()
  } catch (error) {
    message.error('呼叫人工客服失败，请稍后再试')
  } finally {
    handoffLoading.value = false
  }
}

const startNewConversation = async () => {
  loading.value = true
  try {
    conversation.value = await supportApi.getActiveConversation()
    messages.value = []
    draft.value = ''
    autoReplies.value = []
    searched.value = false
    lastAutoReplyQuery.value = ''
    faqPanelOpen.value = false
    await loadMessages()
  } finally {
    loading.value = false
  }
}

const openAccountLink = (item: SupportMessage) => {
  const accountId = Number(item.payload?.account_id)
  if (!accountId) return
  expanded.value = false
  router.push(`/support/account-link/${accountId}`)
}

const handleEnter = (event: KeyboardEvent) => {
  if (!event.shiftKey) {
    event.preventDefault()
    send()
  }
}

watch(visible, (isVisible) => {
  if (isVisible) {
    schedulePolling()
  } else {
    stopPolling()
  }
})

onMounted(async () => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  await initConversation()
  startPolling()
})

onBeforeUnmount(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped lang="less">
.user-support-widget {
  position: fixed;
  right: 22px;
  top: 50%;
  bottom: auto;
  z-index: 1290;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
  transform: translateY(-50%);
}

.support-chat-popover,
.support-float-button {
  pointer-events: auto;
}

.support-float-button {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 52px;
  min-width: 52px;
  height: 52px;
  padding: 0;
  border: 1px solid rgba(56, 189, 248, 0.52);
  border-radius: 50%;
  background:
    radial-gradient(120% 160% at 0 0, rgba(45, 212, 191, 0.28), transparent 58%),
    linear-gradient(135deg, rgba(14, 165, 233, 0.94), rgba(15, 23, 42, 0.96));
  color: #f8fafc;
  font-size: 10px;
  font-weight: 900;
  cursor: pointer;
  box-shadow:
    0 18px 44px rgba(2, 6, 23, 0.42),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
}

.support-float-button .support-float-label {
  display: inline-block;
  font-size: 10px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}

.support-float-button.unread {
  border-color: rgba(45, 212, 191, 0.9);
  box-shadow:
    0 18px 44px rgba(2, 6, 23, 0.42),
    0 0 20px rgba(45, 212, 191, 0.34);
}

.support-float-button :deep(.anticon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: 21px;
  line-height: 1;
}

.support-float-button em {
  position: absolute;
  top: -9px;
  right: -7px;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  line-height: 24px;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.42);
}

.support-chat-popover {
  width: 390px;
  height: min(640px, calc(100vh - 108px));
  display: grid;
  grid-template-rows: auto auto auto minmax(0, 1fr) auto;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 16px;
  background:
    radial-gradient(420px 220px at 0 0, rgba(45, 212, 191, 0.16), transparent 62%),
    rgba(15, 23, 42, 0.97);
  color: #e5f2ff;
  box-shadow:
    0 26px 70px rgba(2, 6, 23, 0.54),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;
  backdrop-filter: blur(16px);
}

.support-chat-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 11px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
}

.support-chat-head p,
.faq-panel-title span {
  margin: 0 0 4px;
  color: #38bdf8;
  font-size: 12px;
  font-weight: 800;
}

.support-chat-head strong,
.faq-panel-title strong {
  color: #f8fafc;
  font-size: 16px;
}

.head-actions {
  display: flex;
  gap: 6px;
}

.head-actions button {
  height: 26px;
  padding: 0 8px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.62);
  color: #cfe0f5;
  font-size: 12px;
  cursor: pointer;
}

.support-status {
  padding: 9px 14px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.72);
  color: #cfe0f5;
  font-size: 12px;
  line-height: 1.45;
}

.support-status.smart { color: #bbf7d0; }
.support-status.waiting { color: #fde68a; }
.support-status.assigned { color: #bfdbfe; }
.support-status.closed { color: #fecaca; }

.support-tools {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(10, 18, 31, 0.72);
}

.faq-tabs {
  min-width: 0;
  display: flex;
  gap: 6px;
  overflow-x: auto;
}

.faq-tabs::-webkit-scrollbar {
  height: 0;
}

.faq-main,
.faq-chip {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(52, 211, 153, 0.28);
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.1);
  color: #bbf7d0;
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;
}

.faq-main,
.faq-chip.active {
  border-color: rgba(52, 211, 153, 0.5);
  background: rgba(34, 197, 94, 0.18);
  color: #dcfce7;
}

:deep(.human-btn) {
  height: 30px;
  border-radius: 999px !important;
  border: 1px solid rgba(52, 211, 153, 0.48) !important;
  background: linear-gradient(135deg, rgba(32, 197, 123, 0.98), rgba(20, 184, 166, 0.9)) !important;
  color: #06151f !important;
  font-size: 12px;
  font-weight: 900;
}

:deep(.human-btn[disabled]) {
  border-color: rgba(148, 163, 184, 0.18) !important;
  background: rgba(30, 41, 59, 0.68) !important;
  color: rgba(203, 213, 225, 0.62) !important;
}

.support-chat-body {
  overflow-y: auto;
  padding: 14px;
  background: rgba(7, 13, 24, 0.3);
}

.support-empty {
  padding: 28px 10px;
  color: #9facbf;
  font-size: 13px;
  text-align: center;
}

.support-empty.compact {
  padding: 12px 4px;
}

.faq-panel,
.auto-reply-panel,
.auto-reply-empty {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid rgba(32, 197, 123, 0.2);
  border-radius: 12px;
  background: rgba(12, 32, 45, 0.84);
}

.faq-panel-title {
  margin-bottom: 10px;
}

.faq-card {
  padding: 10px;
  border-radius: 10px;
  background: rgba(20, 32, 50, 0.92);
}

.faq-card + .faq-card {
  margin-top: 8px;
}

.faq-card h3 {
  margin: 0 0 6px;
  color: #f4f8ff;
  font-size: 14px;
}

.faq-card p,
.auto-reply-empty {
  margin: 0;
  color: #cfe0f5;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.faq-card button {
  margin-top: 9px;
  padding: 5px 9px;
  border: 1px solid rgba(52, 211, 153, 0.28);
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.12);
  color: #bbf7d0;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.reply-images {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 8px;
}

.reply-images img {
  width: 96px;
  height: 72px;
  object-fit: cover;
  border: 1px solid rgba(179, 199, 225, 0.18);
  border-radius: 8px;
}

.message-row {
  display: flex;
  margin-bottom: 12px;
}

.message-row.mine {
  justify-content: flex-end;
}

.message-bubble {
  max-width: 86%;
  padding: 10px 12px;
  border: 1px solid rgba(179, 199, 225, 0.16);
  border-radius: 12px;
  background: rgba(30, 43, 62, 0.94);
  color: #eef4ff;
}

.message-row.mine .message-bubble {
  border-color: rgba(32, 197, 123, 0.34);
  background: linear-gradient(135deg, #17a967, #22c984);
  color: #06151f;
}

.message-row.account-link-row .message-bubble {
  width: 100%;
  max-width: 100%;
  padding: 7px;
  border-color: rgba(49, 221, 198, 0.18);
  background: rgba(8, 16, 29, 0.72);
}

.message-meta {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 5px;
  color: rgba(238, 244, 255, 0.62);
  font-size: 11px;
}

.message-row.mine .message-meta {
  color: rgba(6, 21, 31, 0.62);
}

.message-bubble p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.support-account-card {
  width: 100%;
  display: grid;
  gap: 7px;
  padding: 10px;
  border: 1px solid rgba(49, 221, 198, 0.22);
  border-radius: 12px;
  background:
    linear-gradient(135deg, rgba(17, 31, 50, 0.98), rgba(8, 18, 33, 0.98)),
    radial-gradient(circle at 10% 10%, rgba(45, 212, 191, 0.18), transparent 34%);
  color: #eef8ff;
  text-align: left;
  cursor: pointer;
}

.support-account-card-head,
.support-account-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.support-account-card-head span {
  color: #9eeef0;
  font-size: 12px;
  font-weight: 900;
}

.support-account-card-head em {
  flex: 0 0 auto;
  padding: 3px 7px;
  border-radius: 999px;
  background: rgba(45, 212, 191, 0.14);
  color: #a7f3d0;
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
}

.support-account-card-head em.pending {
  background: rgba(96, 165, 250, 0.14);
  color: #bfdbfe;
}

.support-account-card h3 {
  margin: 0;
  color: #fff;
  font-size: 14px;
  line-height: 1.25;
}

.support-account-card-summary,
.support-account-card-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.support-account-card-stats {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.support-account-card-summary span,
.support-account-card-stats span {
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 9px;
  background: rgba(15, 27, 45, 0.78);
}

.support-account-card small {
  display: block;
  margin-bottom: 2px;
  color: rgba(189, 213, 230, 0.66);
  font-size: 10px;
}

.support-account-card strong {
  display: block;
  overflow: hidden;
  color: #eaf7ff;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-account-card-summary strong {
  color: #5eead4;
  font-size: 14px;
}

.support-account-card-foot {
  justify-content: flex-end;
}

.support-account-card-foot > span {
  padding: 6px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #22d3ee, #20c57b);
  color: #04131d;
  font-size: 11px;
  font-weight: 900;
}

.support-composer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid rgba(179, 199, 225, 0.14);
  background: rgba(14, 23, 38, 0.96);
}

.support-composer :deep(.ant-input) {
  border-color: rgba(179, 199, 225, 0.2);
  background: rgba(8, 15, 27, 0.86);
  color: #f4f8ff;
  font-size: 13px;
}

.support-composer :deep(.ant-btn-primary) {
  border-radius: 8px;
  background: linear-gradient(135deg, #22d3ee, #20c57b);
  color: #04131d;
  font-weight: 900;
}

@media (max-width: 768px) {
  .user-support-widget {
    right: 14px;
    top: auto;
    bottom: 78px;
    transform: none;
  }

  .support-float-button {
    width: 48px;
    min-width: 48px;
    height: 48px;
    padding: 0;
    border-radius: 50%;
  }

}
</style>
